import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useLocation, useNavigationType } from "react-router-dom";
import { api, cardName } from "@/lib/api";
import { useProactiveResolve } from "@/lib/imageFallback";
import { Button } from "@/components/ui/button";
import { ArrowUp, ArrowRight, SlidersHorizontal, X as XIcon } from "lucide-react";
import { useGame, GAMES, useActiveGames } from "@/lib/game";
import { GameBadge } from "@/components/GameBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { SearchTypeahead } from "@/components/SearchTypeahead";
import { Seo } from "@/components/Seo";
import { cardPath } from "@/lib/cardUrl";
import { HitButton, HitSectionLoading, HitSkeletonGrid, useLoadingLevel } from "@/components/loading";
import { HitCardTile } from "@/components/catalog/HitCardTile";
import { CatalogHeader, ActiveFilterChips } from "@/components/catalog/CatalogHeader";
import { avatarBus } from "@/components/avatar/HitAvatar";

// Cache en memoria del último catálogo cargado — se guarda continuamente
// mientras el usuario navega y se restaura al volver desde CardDetail. La
// clave es la firma completa de filtros para que un cambio real (juego,
// query, idioma...) invalide el cache y fuerce recarga.
//
// Separamos "contenido" (variants/offset/hasMore) de "posición" (scrollY):
//   · content se actualiza cuando cambia el estado de la página
//   · scrollY lo actualiza el listener de scroll (nunca desde un effect
//     de contenido, porque cuando restauramos programáticamente el
//     scroll, el window.scrollY todavía es 0 y contaminaríamos el cache)
// SessionStorage-backed cache (Feb 2026): sobrevive F5 en CardDetail y
// swap de tab del browser en móvil. TTL suave 30min (evita restaurar scroll
// de hace 3 días). Cap 500 variants por bucket para no saturar (5MB total).
// Escritura throttled 500ms para no serializar 500 objetos en cada scroll.
const _CATALOG_STATE_CACHE = { key: null, state: null };
const _CATALOG_CAP = 500;
const _CATALOG_TTL_MS = 30 * 60 * 1000;

const _catalogSSKey = (cacheKey) => `hittcg:catalog:${cacheKey}`;
const _catalogSSRead = (cacheKey) => {
  try {
    const raw = sessionStorage.getItem(_catalogSSKey(cacheKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.savedAt) return null;
    if (Date.now() - parsed.savedAt > _CATALOG_TTL_MS) return null;
    return parsed;
  } catch { return null; }
};
const _catalogSSWrite = (cacheKey, state) => {
  try {
    const trimmed = {
      ...state,
      variants: Array.isArray(state.variants) ? state.variants.slice(0, _CATALOG_CAP) : [],
      savedAt: Date.now(),
    };
    sessionStorage.setItem(_catalogSSKey(cacheKey), JSON.stringify(trimmed));
  } catch { /* quota exceeded — el módulo cache sobrevive */ }
};
const _catalogCacheKey = (parts) => "v2:" + parts.map((p) => p ?? "").join("|");

// Estilos por rareza Riftbound. Legend y Overnumbered son los tiers tope:
// - Legend  → dorado (Piltover Enforcer, Kaisa, Yasuo, Jax son "type: Legend"
//   en la API aunque su `rarity` reporte "rare" — el backend ya promueve
//   la rareza efectiva a `"legend"` cuando type==Legend).
// - Overnumbered → naranja (variants especiales, Alt-Art borderless).
// Las demás usan el badge ámbar estándar.
export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const navType = useNavigationType();
  const routeState = useLocation().state;
  // Solo restauramos scroll al volver (atrás o "Volver" del detalle), no al entrar desde el menú.
  const allowRestoreRef = useRef(navType === "POP" || !!routeState?.restoreCatalog);
  const { activeGame, setActiveGame } = useGame();
  const activeGames = useActiveGames();
  const [q, setQ] = useState(params.get("q") || "");
  // Feb 2026 v9 — Filtros ahora VIVEN en la URL (source of truth).
  // Sobreviven back-navigation, refresh y compartir enlace. La state
  // interna se inicializa DESDE la URL para restaurar al volver del
  // detalle de una carta.
  const [language, setLanguage] = useState(() => params.get("lang") || "all");
  const [typeFilter, setTypeFilter] = useState(() => params.get("type") || "all");
  const [gradedHouse, setGradedHouse] = useState(() => params.get("graded_house") || "all");
  const [rarity, setRarity] = useState(() => params.get("rarity") || "all");
  const [rarityOptions, setRarityOptions] = useState([]);
  const [minPriceInput, setMinPriceInput] = useState(() => params.get("min_price") || "");
  const [maxPriceInput, setMaxPriceInput] = useState(() => params.get("max_price") || "");
  const [priceError, setPriceError] = useState("");
  const [province, setProvince] = useState(() => params.get("province") || "all");
  const [provinceOptions, setProvinceOptions] = useState([]);
  // Feb 2026 v9 — DRAFT state para el panel (idioma/tipo/rareza/precio/
  // provincia). El usuario los cambia LIBREMENTE en el panel y solo se
  // aplican al catálogo cuando toca "Aplicar". Antes cada tecla disparaba
  // una request y el catálogo se recargaba — jittery y confuso.
  const [draftLanguage, setDraftLanguage] = useState(language);
  const [draftTypeFilter, setDraftTypeFilter] = useState(typeFilter);
  const [draftGradedHouse, setDraftGradedHouse] = useState(gradedHouse);
  const [draftRarity, setDraftRarity] = useState(rarity);
  const [draftMinPriceInput, setDraftMinPriceInput] = useState(minPriceInput);
  const [draftMaxPriceInput, setDraftMaxPriceInput] = useState(maxPriceInput);
  const [draftProvince, setDraftProvince] = useState(province);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Feb 2026 v9 — Popover en desktop, Sheet en móvil. `useMediaQuery`
  // inline: escuchamos change para reactuar al resize de la ventana.
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  const [variants, setVariants] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const catLevel = useLoadingLevel(loading);
  const catBusy = loading || catLevel !== "none";
  const moreLevel = useLoadingLevel(loadingMore);
  const [loadError, setLoadError] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const PAGE_SIZE = 100;

  // Avatar del encabezado en "thinking" mientras se escribe o se edita un filtro.
  const thinkSig = [q, draftLanguage, draftTypeFilter, draftGradedHouse, draftRarity, draftMinPriceInput, draftMaxPriceInput, draftProvince].join("|");
  const thinkFirst = useRef(true);
  useEffect(() => {
    if (thinkFirst.current) { thinkFirst.current = false; return undefined; }
    avatarBus.thinking(true);
    const t = setTimeout(() => avatarBus.thinking(false), 1500);
    return () => clearTimeout(t);
  }, [thinkSig]);
  useEffect(() => () => avatarBus.thinking(false), []);

  // Precios parseados (o null si vacío/no numérico). Se usan para
  // (a) mandarlos al backend y (b) armar la cacheKey.
  const parsedMinPrice = minPriceInput.trim() === "" ? null : Number(minPriceInput);
  const parsedMaxPrice = maxPriceInput.trim() === "" ? null : Number(maxPriceInput);
  const priceRangeInvalid =
    parsedMinPrice !== null && parsedMaxPrice !== null &&
    Number.isFinite(parsedMinPrice) && Number.isFinite(parsedMaxPrice) &&
    parsedMaxPrice < parsedMinPrice;

  // Firma del estado actual — cualquier cambio invalida el cache al volver.
  const cacheKey = _catalogCacheKey([
    params.get("q") || "", language, typeFilter, gradedHouse, rarity, activeGame,
    parsedMinPrice ?? "", parsedMaxPrice ?? "", province,
  ]);
  // `mountReadyRef` = true después del primer efecto de mount (haya
  // restaurado o hecho load). Antes de eso, el effect de persist NO debe
  // escribir en el cache — si lo hace, sobreescribe el cache bueno con
  // el estado inicial vacío justo cuando el usuario acaba de volver.
  const mountReadyRef = useRef(false);

  const applyClientFilters = (data) => {
    // Feb 2026: los filtros AHORA se aplican server-side (idioma, tipo,
    // casa gradeadora, rareza). Este helper queda solo como identidad para
    // no romper el diff — TODO: remover en la próxima limpieza si nada más
    // lo usa. La regla dura de segmentación por juego también vive en
    // backend (`_card_ids_for_game`).
    return data;
  };

  // Firma de filtros activos — usada para (a) diferenciar el mensaje "no hay
  // resultados" (mercado recién arranca vs. filtros muy estrictos) y (b)
  // pasar exactamente los mismos params al backend en `load` y `loadMore`.
  const hasActiveFilter = language !== "all" || typeFilter !== "all" || gradedHouse !== "all" || rarity !== "all" || !!(params.get("q") || "").trim() || parsedMinPrice !== null || parsedMaxPrice !== null || province !== "all";
  // Contador de filtros activos que van DENTRO del panel (excluye la
  // búsqueda de texto, que vive afuera y es la acción principal).
  const activeFilterCount = (
    (language !== "all" ? 1 : 0) +
    (typeFilter !== "all" ? 1 : 0) +
    (typeFilter === "graded" && gradedHouse !== "all" ? 1 : 0) +
    (rarity !== "all" ? 1 : 0) +
    (parsedMinPrice !== null ? 1 : 0) +
    (parsedMaxPrice !== null ? 1 : 0) +
    (province !== "all" ? 1 : 0)
  );
  const LANG_LABEL = { es: "Español", en: "Inglés", ja: "Japonés", otro: "Otro idioma" };
  const TYPE_LABEL = { cards: "Solo cartas", sealed: "Solo sellados", bundles: "Solo colecciones", graded: "Solo gradeadas" };
  const filterChips = [
    language !== "all" && { key: "language", label: LANG_LABEL[language] || language, remove: () => setLanguage("all") },
    typeFilter !== "all" && { key: "type", label: TYPE_LABEL[typeFilter] || typeFilter, remove: () => { setTypeFilter("all"); setGradedHouse("all"); } },
    typeFilter === "graded" && gradedHouse !== "all" && { key: "house", label: gradedHouse, remove: () => setGradedHouse("all") },
    rarity !== "all" && { key: "rarity", label: rarity, remove: () => setRarity("all") },
    parsedMinPrice !== null && { key: "min", label: `Desde $${parsedMinPrice}`, remove: () => setMinPriceInput("") },
    parsedMaxPrice !== null && { key: "max", label: `Hasta $${parsedMaxPrice}`, remove: () => setMaxPriceInput("") },
    province !== "all" && { key: "province", label: province, remove: () => setProvince("all") },
  ].filter(Boolean);
  const clearAllFilters = () => {
    // v9 — limpia AMBOS: draft (panel abierto) y state (URL). Solo escribe
    // URL si algo cambió, para no meter una entrada extra en el history.
    setDraftLanguage("all");
    setDraftTypeFilter("all");
    setDraftGradedHouse("all");
    setDraftRarity("all");
    setDraftMinPriceInput("");
    setDraftMaxPriceInput("");
    setDraftProvince("all");
    setLanguage("all");
    setTypeFilter("all");
    setGradedHouse("all");
    setRarity("all");
    setMinPriceInput("");
    setMaxPriceInput("");
    setProvince("all");
  };
  // v9 — Aplicar: copia DRAFT → state real (que dispara la carga + sync URL).
  const applyFilters = () => {
    setLanguage(draftLanguage);
    setTypeFilter(draftTypeFilter);
    setGradedHouse(draftTypeFilter === "graded" ? draftGradedHouse : "all");
    setRarity(draftRarity);
    setMinPriceInput(draftMinPriceInput);
    setMaxPriceInput(draftMaxPriceInput);
    setProvince(draftProvince);
    setFiltersOpen(false);
  };
  // v9 — al abrir el panel, resetea DRAFT ← state actual, para que el
  // usuario vea los filtros ya aplicados (no un panel vacío).
  useEffect(() => {
    if (filtersOpen || isDesktop) {
      setDraftLanguage(language);
      setDraftTypeFilter(typeFilter);
      setDraftGradedHouse(gradedHouse);
      setDraftRarity(rarity);
      setDraftMinPriceInput(minPriceInput);
      setDraftMaxPriceInput(maxPriceInput);
      setDraftProvince(province);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersOpen, isDesktop, language, typeFilter, gradedHouse, rarity, minPriceInput, maxPriceInput, province]);
  // v9 — sync state → URL. Se dispara cuando el state real cambia (después
  // de Aplicar o cambios directos como cambio de juego). El URL se vuelve
  // el "guardián" que sobrevive back/refresh/compartir.
  useEffect(() => {
    const next = new URLSearchParams(params);
    const upsert = (key, val, def) => {
      if (val !== null && val !== undefined && val !== "" && val !== def) next.set(key, String(val));
      else next.delete(key);
    };
    upsert("lang", language, "all");
    upsert("type", typeFilter, "all");
    upsert("graded_house", typeFilter === "graded" ? gradedHouse : "all", "all");
    upsert("rarity", rarity, "all");
    upsert("min_price", minPriceInput, "");
    upsert("max_price", maxPriceInput, "");
    upsert("province", province, "all");
    if (next.toString() !== params.toString()) {
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, typeFilter, gradedHouse, rarity, minPriceInput, maxPriceInput, province]);
  const buildFilterParams = () => {
    const p = {};
    if (language !== "all") p.language = language;
    if (typeFilter !== "all") p.type_filter = typeFilter;
    if (typeFilter === "graded" && gradedHouse !== "all") p.graded_house = gradedHouse;
    if (rarity !== "all") p.rarity = rarity;
    // Precio: solo mandamos números finitos válidos. Si "hasta<desde",
    // omitimos el filtro (además pintamos el aviso en la UI).
    if (parsedMinPrice !== null && Number.isFinite(parsedMinPrice) && !priceRangeInvalid) p.min_price = parsedMinPrice;
    if (parsedMaxPrice !== null && Number.isFinite(parsedMaxPrice) && !priceRangeInvalid) p.max_price = parsedMaxPrice;
    if (province !== "all") p.province = province;
    return p;
  };

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.get("/catalog/variants", {
        params: { q: params.get("q") || "", limit: PAGE_SIZE, offset: 0, game: activeGame, ...buildFilterParams() },
      });
      const data = r.data || [];
      // Backend paginación unificada (feb 2026 — opción A canónica):
      // la respuesta ya viene con `in_stock DESC` como primer criterio de
      // sort sobre TODA la base filtrada, ya rebanada al slice
      // `[offset:offset+limit]`. El frontend NO reordena — confía en el
      // orden del backend. `offset` avanza con TODO lo devuelto (activas
      // + agotadas). `hasMore` deriva de si llenó la página.
      setVariants(data);
      setOffset(data.length);
      setHasMore(data.length >= PAGE_SIZE);
    } catch (err) {
      // Antes: try/finally sin catch → si el backend fallaba (401 con token
      // caducado, 500, timeout, CORS…) el usuario veía la página vacía sin
      // ninguna señal. Ahora capturamos el error, mostramos un banner claro
      // y ofrecemos reintentar. Aplica también a visitantes sin cuenta.
      // Ignoramos errores de aborto (cambio de juego mid-request) — no son
      // errores reales, solo indican que llegó otra petición más nueva.
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      setLoadError(
        status && status >= 500
          ? "El catálogo está teniendo problemas técnicos. Intenta de nuevo en un momento."
          : detail || err?.message || `No pudimos cargar el catálogo de ${activeGame === "riftbound" ? "Riftbound" : activeGame === "yugioh" ? "Yu-Gi-Oh!" : "Pokémon"}. Revisa tu conexión e intenta de nuevo.`
      );
      setVariants([]);
      setHasMore(false);
    } finally { setLoading(false); }
  };

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await api.get("/catalog/variants", {
        // FIX CRÍTICO (feb 2026): faltaba pasar `game` en la 2da/3ra página →
        // al hacer scroll se contaminaba el catálogo con cartas del otro juego
        // (bug de Piltover Enforcer apareciendo entre Pokémon). El filtro por
        // juego debe aplicarse en TODAS las páginas, no solo la primera.
        // Feb 2026 (server-side filters): además del juego, propagamos todos
        // los filtros (idioma, tipo, casa gradeadora, rareza) a cada página
        // para que la paginación sea consistente y no aparezcan cartas que
        // el filtro debería excluir.
        params: { q: params.get("q") || "", limit: PAGE_SIZE, offset, game: activeGame, ...buildFilterParams() },
      });
      const data = r.data || [];
      // Backend paginación unificada (feb 2026, opción A canónica): las
      // agotadas ya vienen ordenadas AL FINAL del slice global — no
      // necesitamos re-separar por `in_stock` en el frontend.
      // Solo dedupeamos por key y anexamos al final del array previo.
      const keyOf = (v) => `${v.card_id}|${v.language}|${v.variant}|${v.graded_house || ""}|${v.graded_grade || ""}`;
      setVariants((prev) => {
        const seen = new Set(prev.map(keyOf));
        const merged = [...prev];
        for (const v of data) {
          const k = keyOf(v);
          if (!seen.has(k)) {
            seen.add(k);
            merged.push(v);
          }
        }
        return merged;
      });
      setOffset(offset + data.length);
      setHasMore(data.length >= PAGE_SIZE);
    } finally { setLoadingMore(false); }
  };

  useEffect(() => {
    // Cache invalidation explícito (feb 2026): al cambiar cualquier filtro
    // (idioma, tipo, gradedHouse, rareza, query, juego), la `cacheKey` cambia.
    // Barremos TODAS las entradas de sessionStorage con prefijo del catálogo
    // que no correspondan al cacheKey actual. Esto elimina cualquier chance
    // de mostrar contenido cacheado con criterios distintos — aunque la
    // cacheKey ya incluye los filtros, este barrido es la garantía extra.
    try {
      const currentSSKey = _catalogSSKey(cacheKey);
      const stale = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith("hittcg:catalog:") && k !== currentSSKey) {
          stale.push(k);
        }
      }
      stale.forEach((k) => sessionStorage.removeItem(k));
      if (_CATALOG_STATE_CACHE.key && _CATALOG_STATE_CACHE.key !== cacheKey) {
        _CATALOG_STATE_CACHE.key = null;
        _CATALOG_STATE_CACHE.state = null;
      }
    } catch { /* noop */ }
  }, [cacheKey]);

  useEffect(() => {
    // Feb 2026 — listener para invalidación externa disparada por
    // PublishCard/SellerDashboard tras publicar/editar/eliminar. Sin esto
    // el usuario volvía al catálogo tras publicar y NO veía su carta hasta
    // cerrar la pestaña (bug reportado con Riftbound). El event limpia
    // AMBOS caches (memoria + sessionStorage) y forza un reload fresh.
    const handler = () => {
      try {
        _CATALOG_STATE_CACHE.key = null;
        _CATALOG_STATE_CACHE.state = null;
        const toDelete = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const k = sessionStorage.key(i);
          if (k && k.startsWith("hittcg:catalog:")) toDelete.push(k);
        }
        toDelete.forEach((k) => sessionStorage.removeItem(k));
      } catch { /* noop */ }
      // Fresh load con los filtros actuales.
      if (mountReadyRef.current) {
        try { load(); } catch { /* noop */ }
      }
    };
    window.addEventListener("hittcg:catalog:invalidate", handler);
    return () => window.removeEventListener("hittcg:catalog:invalidate", handler);
  }, [cacheKey]);

  useEffect(() => {
    // Init / restauración al volver desde CardDetail.
    //
    // Estrategia: si en el cache hay contenido con la MISMA firma de
    // filtros y todavía no hemos hecho el primer mount (mountReadyRef=
    // false), restauramos ese snapshot y scrolleamos a `scrollY`. Si no
    // hay cache o cambió la firma → cargamos fresh.
    //
    // No usamos `useNavigationType()` porque es frágil: cuando CardDetail
    // usa un <Link> (PUSH) o cuando el router hace REPLACE por cualquier
    // razón, "POP" no aplica y perdíamos el estado aunque el cache
    // estuviera fresco. Con cache-key basado en filtros ya tenemos la
    // señal correcta para saber si es seguro restaurar.
    if (!mountReadyRef.current) {
      // Feb 2026: primero módulo (mismo tab, rápido); si no, sessionStorage
      // (sobrevive F5 y swap de tab). Ambos con la misma cacheKey.
      let cached = _CATALOG_STATE_CACHE.key === cacheKey ? _CATALOG_STATE_CACHE.state : null;
      if (!cached) {
        cached = _catalogSSRead(cacheKey);
        if (cached) {
          _CATALOG_STATE_CACHE.key = cacheKey;
          _CATALOG_STATE_CACHE.state = cached;
        }
      }
      if (cached && Array.isArray(cached.variants) && cached.variants.length > 0 && allowRestoreRef.current) {
        setVariants(cached.variants);
        setOffset(cached.offset || 0);
        setHasMore(!!cached.hasMore);
        setLoading(false);
        mountReadyRef.current = true;
        // Doble rAF + clamp: si el contenido restaurado es más corto que
        // cuando se guardó el scrollY (ej. cap 500 truncó cartas), scrolleamos
        // al máximo posible del documento en vez de apuntar al vacío al fondo.
        const y = cached.scrollY || 0;
        // Espera a que la grilla restaurada tenga altura suficiente (hasta 1.5 s) antes de scrollear;
        // si no, el clamp apunta al footer y el scroll-anchoring lo deja pegado abajo.
        const t0 = performance.now();
        const tryRestore = () => {
          const maxScroll = Math.max(0, (document.documentElement.scrollHeight || 0) - window.innerHeight);
          if (maxScroll < y && performance.now() - t0 < 1500) { requestAnimationFrame(tryRestore); return; }
          // Si la grilla no alcanzó la altura guardada, no movemos nada (nunca saltar al footer).
          if (maxScroll < y) return;
          const target = y;
          window.scrollTo({ top: target, behavior: "auto" });
          if (cached.hasMore && (maxScroll - target) < 300) {
            setTimeout(() => { try { load(true); } catch { /* noop */ } }, 100);
          }
        };
        requestAnimationFrame(tryRestore);
        return;
      }
    }
    mountReadyRef.current = true;
    load();
  }, [params, language, typeFilter, gradedHouse, rarity, activeGame, parsedMinPrice, parsedMaxPrice, province]);

  // Persistencia continua del CONTENIDO (no del scroll). Skip mientras
  // variants=[] para no sobreescribir el cache bueno con estado vacío
  // apenas montamos.
  useEffect(() => {
    if (!mountReadyRef.current) return;
    if (!variants || variants.length === 0) return;
    const prev = _CATALOG_STATE_CACHE.key === cacheKey ? (_CATALOG_STATE_CACHE.state || {}) : {};
    _CATALOG_STATE_CACHE.key = cacheKey;    _CATALOG_STATE_CACHE.state = {
      variants,
      offset,
      hasMore,
      scrollY: prev.scrollY || 0,
    };
    // Persistimos a sessionStorage — sobrevive F5 y descarga de tab en móvil.
    _catalogSSWrite(cacheKey, _CATALOG_STATE_CACHE.state);
  }, [cacheKey, variants, offset, hasMore]);

  useEffect(() => {
    // Guardado continuo del scroll. Este es el ÚNICO lugar donde se
    // escribe scrollY en el cache. rAF-throttle mientras el usuario mueve.
    // SessionStorage también con throttle 500ms para evitar serializar
    // 500 variants en cada scroll frame (perf móvil).
    let raf = 0;
    let ssTimer = 0;
    const flushSS = () => {
      if (_CATALOG_STATE_CACHE.state && _CATALOG_STATE_CACHE.key === cacheKey) {
        _catalogSSWrite(cacheKey, _CATALOG_STATE_CACHE.state);
      }
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const y = window.scrollY || 0;
        if (_CATALOG_STATE_CACHE.state && _CATALOG_STATE_CACHE.key === cacheKey) {
          _CATALOG_STATE_CACHE.state.scrollY = y;
          if (ssTimer) clearTimeout(ssTimer);
          ssTimer = setTimeout(flushSS, 500);
        }
        setShowBackToTop(y > 600);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
      if (ssTimer) { clearTimeout(ssTimer); flushSS(); }
    };
  }, [cacheKey]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // Cargamos las rarezas realmente presentes en el catálogo activo — el
  // endpoint las saca de la DB, no de una lista inventada. Filtra por juego:
  // en Riftbound solo aparecen common/uncommon/rare/epic/overnumbered; en
  // Pokémon las rarezas de TCGdex (Holo Rare, VMAX, etc.).
  useEffect(() => {
    api.get("/catalog/rarities", { params: { game: activeGame } })
      .then((r) => setRarityOptions(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRarityOptions([]));
    // Al cambiar de juego, reseteamos rareza (Pokémon/Riftbound tienen
    // taxonomías distintas). v9: escribimos también en draft para que
    // si el panel está abierto no muestre una rareza "fantasma".
    setRarity("all");
    setDraftRarity("all");
  }, [activeGame]);

  // Provincias con producto — el dropdown se arma dinámicamente y solo
  // muestra provincias con ≥1 publicación activa del juego actual.
  useEffect(() => {
    api.get("/catalog/provinces-with-stock", { params: { game: activeGame } })
      .then((r) => setProvinceOptions(Array.isArray(r.data) ? r.data : []))
      .catch(() => setProvinceOptions([]));
    // Reset al cambiar de juego para no arrastrar una provincia que en el
    // otro juego no tiene stock.
    setProvince("all");
    setDraftProvince("all");
  }, [activeGame]);

  // Aviso "hasta<desde" — aplicado sobre el DRAFT (dentro del panel).
  // No bloquea el submit; solo pintamos mensaje. Aplicar queda deshabilitado
  // cuando el rango es inválido.
  const draftMinNum = draftMinPriceInput.trim() === "" ? null : Number(draftMinPriceInput);
  const draftMaxNum = draftMaxPriceInput.trim() === "" ? null : Number(draftMaxPriceInput);
  const draftPriceInvalid =
    draftMinNum !== null && draftMaxNum !== null &&
    Number.isFinite(draftMinNum) && Number.isFinite(draftMaxNum) &&
    draftMaxNum < draftMinNum;
  useEffect(() => {
    setPriceError(draftPriceInvalid ? "El precio 'Hasta' no puede ser menor que 'Desde'." : "");
  }, [draftPriceInvalid]);

  const submitSearch = (e) => {
    e.preventDefault();
    // v9 — preservar filtros al buscar. Antes: `setParams({ q })` NUKEABA
    // todo (idioma, precio, provincia). Ahora sobrescribimos solo `q`.
    const next = new URLSearchParams(params);
    if (q && q.trim()) next.set("q", q.trim()); else next.delete("q");
    setParams(next);
  };

  // v9 — Contenido del panel (compartido entre Popover desktop y Sheet
  // mobile). Vive como JSX const para no duplicar el markup. Usa DRAFT
  // state, así el usuario puede tocar los controles libremente hasta que
  // toca "Aplicar" — no se dispara ninguna request intermedia.

  // v23 — Resolución PROACTIVA de imagen para cartas API sin `image_url`
  // en db.cards. Consulta `/api/img/verify-fallback` por cada card con
  // set_id+number sin image_url. Rescata cartas de sets modernos aunque
  // el mirror TCGdex esté stale. Cero cambios en backend (endpoint dedicado).
  const resolvedByCardId = useProactiveResolve(variants, (v) => v.card);

  const fieldCls = (on) => `w-full h-12 rounded-[12px] bg-[#141414] text-white ${on ? "border-[#FF4438]" : "border-white/10"}`;
  const inputFieldCls = (on) => `min-w-0 w-0 flex-1 h-12 rounded-[12px] bg-[#141414] border text-white px-3 text-sm placeholder:text-white/35 focus:outline-none focus:border-[#FF4438] ${on ? "border-[#FF4438]" : "border-white/10"}`;
  const filtersPanel = (
    <>
      <div className="px-5 pt-5 pb-3 border-b border-white/10 flex items-center justify-between gap-3">
        <div className="sx text-white text-[20px] font-extrabold tracking-[-.04em] whitespace-nowrap truncate">
          Filtros <span className="italic font-normal text-white/60">{GAMES[activeGame]?.label}</span>
        </div>
        {isDesktop && activeFilterCount > 0 && (
          <button type="button" onClick={clearAllFilters} className="flex-none text-xs text-white/60 underline underline-offset-2 hover:text-white whitespace-nowrap" data-testid="catalog-clear-filters-panel">Limpiar</button>
        )}
        {!isDesktop && (
          <button type="button" onClick={() => setFiltersOpen(false)} aria-label="Cerrar filtros" data-testid="catalog-close-filters"
            className="flex-none w-9 h-9 rounded-full border border-[rgba(255,255,255,.14)] grid place-items-center text-white/80 hover:text-white hover:bg-white/5 transition-colors">
            <XIcon size={16} />
          </button>
        )}
      </div>
      <div className="px-5 py-4 space-y-5 flex-1">
        <div>
          <label className="sx-label block mb-2">Idioma</label>
          <div className="flex flex-wrap gap-2" role="radiogroup" data-testid="catalog-language-filter">
            {[["all", "Todos los idiomas"], ["es", "Español"], ["en", "Inglés"], ["ja", "Japonés"], ["otro", "Otro"]].map(([val, lbl]) => (
              <button key={val} type="button" role="radio" aria-checked={draftLanguage === val} onClick={() => setDraftLanguage(val)}
                data-testid={`catalog-language-opt-${val}`}
                className={`h-9 px-3.5 rounded-full border text-[13px] font-semibold transition-[background-color,border-color,color] ${draftLanguage === val ? (val === "all" ? "bg-white/10 border-white/40 text-white" : "bg-[rgba(255,68,56,.12)] border-[#FF4438] text-white") : "bg-[#141414] border-white/10 text-white/60 hover:text-white"}`}>
                {lbl}
              </button>
            ))}
          </div>
        </div>
        <div className={`grid gap-3 ${isDesktop ? "grid-cols-1" : "grid-cols-2"}`}>
        <div className="min-w-0">
          <label className="sx-label block mb-2">Tipo</label>
          <Select value={draftTypeFilter} onValueChange={(v) => { setDraftTypeFilter(v); if (v !== "graded") setDraftGradedHouse("all"); }}>
            <SelectTrigger data-testid="catalog-type-filter" className={fieldCls(draftTypeFilter !== "all")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#171717] border-[#262626] text-white">
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="cards">Solo cartas</SelectItem>
              <SelectItem value="sealed">Solo sellados</SelectItem>
              <SelectItem value="bundles">Solo colecciones</SelectItem>
              <SelectItem value="graded">Solo gradeadas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {draftTypeFilter !== "sealed" && rarityOptions.length > 0 && (
          <div className="min-w-0">
            <label className="sx-label block mb-2">Rareza</label>
            <Select value={draftRarity} onValueChange={setDraftRarity}>
              <SelectTrigger data-testid="catalog-rarity-filter" className={fieldCls(draftRarity !== "all")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#171717] border-[#262626] text-white max-h-72 overflow-y-auto">
                <SelectItem value="all">Todas las rarezas</SelectItem>
                {rarityOptions.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        </div>
        {draftTypeFilter === "graded" && (
          <div>
            <label className="sx-label block mb-2">Casa gradeadora</label>
            <Select value={draftGradedHouse} onValueChange={setDraftGradedHouse}>
              <SelectTrigger data-testid="catalog-graded-house-filter" className={fieldCls(draftGradedHouse !== "all")}>
                <SelectValue placeholder="Casa" />
              </SelectTrigger>
              <SelectContent className="bg-[#171717] border-[#262626] text-white">
                <SelectItem value="all">Todas las casas</SelectItem>
                <SelectItem value="PSA">PSA</SelectItem>
                <SelectItem value="BGS">BGS</SelectItem>
                <SelectItem value="CGC">CGC</SelectItem>
                <SelectItem value="SGC">SGC</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <label className="sx-label block mb-2">Precio</label>
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              data-testid="catalog-min-price"
              placeholder="Desde $"
              value={draftMinPriceInput}
              onChange={(e) => setDraftMinPriceInput(e.target.value)}
              className={inputFieldCls(draftMinPriceInput !== "")}
            />
            <input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              data-testid="catalog-max-price"
              placeholder="Hasta $"
              value={draftMaxPriceInput}
              onChange={(e) => setDraftMaxPriceInput(e.target.value)}
              className={inputFieldCls(draftMaxPriceInput !== "")}
            />
          </div>
          {priceError && (
            <div className="mt-2 text-xs text-red-400" data-testid="catalog-price-error">
              {priceError}
            </div>
          )}
        </div>
        {provinceOptions.length > 0 && (
          <div>
            <label className="sx-label block mb-2">Provincia</label>
            <Select value={draftProvince} onValueChange={setDraftProvince}>
              <SelectTrigger data-testid="catalog-province-filter" className={fieldCls(draftProvince !== "all")}>
                <SelectValue placeholder="Provincia" />
              </SelectTrigger>
              <SelectContent className="bg-[#171717] border-[#262626] text-white max-h-72 overflow-y-auto">
                <SelectItem value="all">Todas las provincias</SelectItem>
                {provinceOptions.map((p) => (
                  <SelectItem key={p} value={p} data-testid={`catalog-province-opt-${p}`}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div
        className={`px-5 py-4 border-t border-white/10 flex gap-3 sticky bottom-0 ${isDesktop ? "bg-[#0d0d0d]" : "bg-[#0d0d0d]/95 backdrop-blur"}`}
        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
      >
        {!isDesktop && <Button
          type="button"
          onClick={clearAllFilters}
          disabled={activeFilterCount === 0}
          data-testid="catalog-clear-filters-panel"
          className="flex-1 h-[50px] rounded-full bg-transparent border border-white/15 text-white hover:bg-white/5 disabled:opacity-40"
        >
          <XIcon size={14} className="mr-1" /> Limpiar filtros
        </Button>}
        <Button
          type="button"
          onClick={applyFilters}
          disabled={draftPriceInvalid}
          data-testid="catalog-apply-filters"
          className="flex-1 h-[50px] rounded-full bg-[#FF4438] hover:bg-[#ff5a4f] text-[#080808] font-bold disabled:opacity-40"
        >
          Aplicar
        </Button>
      </div>
    </>
  );

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
      <Seo
        title={activeGame === "riftbound" ? "Catálogo de cartas Riftbound" : activeGame === "yugioh" ? "Catálogo de cartas Yu-Gi-Oh!" : "Catálogo de cartas Pokémon"}
        description={`Explora cartas ${activeGame === "riftbound" ? "Riftbound" : activeGame === "yugioh" ? "Yu-Gi-Oh!" : "Pokémon"} disponibles en Ecuador. Precios reales de mercado publicados por la comunidad ecuatoriana. Compra directo con escrow y envíos a todo el país.`}
        keywords={`catálogo cartas ${activeGame === "riftbound" ? "Riftbound" : activeGame === "yugioh" ? "Yu-Gi-Oh!" : "Pokémon"}, precios Ecuador, comprar cartas TCG, marketplace cartas Ecuador`}
        url="/catalog"
      />
      <CatalogHeader games={activeGames} active={activeGame} onSelect={setActiveGame} />

      <div className="lg:grid lg:grid-cols-[260px_1fr] lg:gap-8">
      {isDesktop && (
        <aside className="sx sticky top-[65px] self-start max-h-[calc(100vh-65px)] overflow-y-auto no-scrollbar bg-[#0d0d0d] border border-white/10 rounded-[22px] flex flex-col" data-testid="catalog-filters-panel">
          {filtersPanel}
        </aside>
      )}
      <div className="min-w-0">
      <form onSubmit={submitSearch} className="flex gap-2.5 mb-4" data-testid="catalog-search-form">
        <div className="relative flex-1 min-w-0">
          <SearchTypeahead
            value={q}
            onChange={setQ}
            onSubmit={submitSearch}
            size="compact"
            testidPrefix="catalog-search"
            placeholder={activeGame === "riftbound"
              ? "Escribe pedazos o con errores ('vi', 'jhin', 'piltover')…"
              : activeGame === "yugioh"
              ? "Busca por nombre en inglés ('dark magician', 'blue-eyes')…"
              : "Escribe pedazos o con errores ('char', 'charizar')…"}
          />
          <button data-testid="catalog-search-btn" type="submit" aria-label="Buscar"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 z-10 h-[38px] min-w-[38px] lg:px-5 rounded-full bg-[#FF4438] hover:bg-[#ff5a4f] text-[#080808] font-bold text-sm inline-flex items-center justify-center gap-1.5 transition-colors">
            <span className="hidden lg:inline">Buscar</span><ArrowRight size={16} className="lg:hidden" />
          </button>
        </div>
        {!isDesktop && (
          <button type="button" onClick={() => setFiltersOpen(true)} data-testid="catalog-open-filters" aria-label="Filtros"
            className={`relative flex-none w-[50px] h-[50px] rounded-[14px] border inline-flex items-center justify-center transition-colors ${activeFilterCount > 0 ? "border-[#FF4438] bg-[rgba(255,68,56,.1)] text-white" : "border-white/10 bg-[#101010] text-white/80"}`}>
            <SlidersHorizontal size={18} />
            {activeFilterCount > 0 && <span className="sx-mono absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-[#FF4438] text-[#080808] text-[10px] grid place-items-center" data-testid="catalog-filters-badge">{activeFilterCount}</span>}
          </button>
        )}
      </form>
      <ActiveFilterChips chips={filterChips} onClear={clearAllFilters} />

      {!isDesktop && (
        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
          <SheetContent
            side="bottom"
            className="sx bg-[#0d0d0d] border-t border-white/10 rounded-t-[28px] text-white max-h-[88vh] overflow-y-auto flex flex-col gap-0 p-0 [&>button.absolute]:hidden"
            data-testid="catalog-filters-panel"
          >
            <div className="pt-3 flex justify-center" aria-hidden><span className="w-10 h-1.5 rounded-full bg-white/20" /></div>
            {filtersPanel}
          </SheetContent>
        </Sheet>
      )}

      {loadError ? (
        <div className="bg-red-500/10 border border-red-500/40 rounded-md p-6 text-center" data-testid="catalog-load-error">
          <div className="text-red-300 font-medium mb-2">No pudimos cargar el catálogo</div>
          <div className="text-sm text-red-200/80 mb-4">{loadError}</div>
          <Button type="button" onClick={load} className="bg-red-600 hover:bg-red-700" data-testid="catalog-load-retry">
            Reintentar
          </Button>
        </div>
      ) : catBusy ? (
        <div className="min-h-[60vh]" data-testid="catalog-skeleton">
          <HitSectionLoading level={catLevel} loading={loading} section={params.get("q") ? "search" : "catalog"} grid desktopCols={4}
                             gridClassName="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-2.5 md:gap-4" onRetry={load} />
        </div>
      ) : variants.length === 0 ? (
        <div className="border border-dashed border-[#262626] rounded-md p-12 text-center text-neutral-500" data-testid="catalog-empty">
          {hasActiveFilter ? (
            <>Ninguna carta coincide con esos filtros. Prueba <button
              type="button"
              onClick={() => { clearAllFilters(); setQ(""); setParams({}); }}
              className="text-[#EF4444] hover:underline"
              data-testid="catalog-clear-filters"
            >quitando algunos criterios</button>.</>
          ) : (
            <>Este mercado recién arranca — <Link to="/publish" className="text-[#EF4444] hover:underline">sube tu primera carta</Link> y sé quien le pone el primer precio en Ecuador.</>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-2.5 md:gap-4" data-testid="catalog-grid">
          {variants.map((v, idx) => {
            const variant = v.variant || "normal";
            const cardImg = v.card?.image_url;
            const idPhoto = v.card?.identification_photos?.[0];
            // Cascada de imagen (feb 2026): (1) TCGdex/admin_image_url del card,
            // (2) foto del carné del artista (custom cards), (3) primera foto
            // de la publicación activa más barata — para que cartas legacy
            // sin image_url no muestren placeholder negro. `first_photo` viene
            // del backend como storage path relativo.
            // (v23) (4) URL resuelta proactivamente desde pokemontcg.io usando
            // set_id + number cuando el mirror no tiene image_url — rescata
            // cartas de sets modernos aunque assets.tcgdex.net esté caído.
            const firstPhoto = v.first_photo;
            const proactiveUrl = resolvedByCardId[v.card_id];
            const imgSrc = cardImg
              ? (cardImg.startsWith("http") ? cardImg
                 : cardImg.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${cardImg}`
                 : `${process.env.REACT_APP_BACKEND_URL}/api/files/public/${cardImg}`)
              : (proactiveUrl ? proactiveUrl
              : (idPhoto ? `${process.env.REACT_APP_BACKEND_URL}/api/files/public/${idPhoto}`
              : (firstPhoto ? `${process.env.REACT_APP_BACKEND_URL}/api/files/public/${firstPhoto}` : null)));
            const gradedKey = v.graded_key || "raw";
            // URL amigable: /carta/{slug}--{cardId}/{lang}[/{variant}]
            const to = cardPath({
              cardId: v.card_id,
              name: cardName(v.card, v.language),
              language: v.language,
              variant,
              graded: gradedKey === "raw" ? undefined : gradedKey,
            });
            const ygoKey = v.yugioh_set_code ? `-${v.yugioh_set_code}-${v.yugioh_rarity || ""}-${v.yugioh_rarity_note || ""}` : "";
            return (
              <HitCardTile
                key={`${v.card_id}-${v.language}-${variant}-${gradedKey}${ygoKey}`}
                v={v} to={to} imgSrc={imgSrc} eager={idx < 4}
                from={`/catalog${params.toString() ? `?${params.toString()}` : ""}`}
                testid={`catalog-card-${v.card_id}-${v.language}-${variant}-${gradedKey}${ygoKey}`}
              />
            );
          })}
        </div>
      )}
      {moreLevel !== "none" && (
        <div className="mt-4" data-testid="catalog-skeleton-more">
          <HitSkeletonGrid rows={1} desktopCols={4} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-2.5 md:gap-4" />
        </div>
      )}
      {hasMore && !catBusy && (
        <div className="mt-8 flex justify-center">
          <HitButton
            as={Button}
            data-testid="catalog-load-more"
            onClick={loadMore}
            loading={loadingMore}
            loadingText="Cargando…"
            className="bg-[#EF4444] hover:bg-[#DC2626] text-white px-8 py-3 gap-2.5"
          >
            {`Cargar más cartas (+${PAGE_SIZE})`}
          </HitButton>
        </div>
      )}
      {!hasMore && !catBusy && variants.length > 0 && (
        <div className="mt-8 text-center text-xs text-neutral-500" data-testid="catalog-end">
          Fin del catálogo · {variants.length} {variants.length === 1 ? "resultado" : "resultados"}
        </div>
      )}
      </div>
      </div>

      {/* Botón flotante "Volver arriba" — aparece cuando el usuario ha
          scrolleado más de 600px. Diseño discreto para no tapar tarjetas.
          Móvil-friendly: safe-area bottom, tamaño táctil generoso. */}
      {showBackToTop && (
        <button
          type="button"
          onClick={scrollToTop}
          data-testid="catalog-back-to-top"
          aria-label="Volver arriba"
          className="fixed bottom-6 right-6 z-30 bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-full shadow-lg shadow-black/50 p-3 sm:p-3.5 transition-all hover:scale-105 active:scale-95"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
        >
          <ArrowUp size={20} />
        </button>
      )}
    </div>
  );
}
